import { useEffect } from "react";

interface WebsiteSchemaProps {
  name?: string;
  description?: string;
  url?: string;
}

interface ArticleSchemaProps {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  author: string;
  url: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  items: FAQItem[];
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

const useStructuredData = (id: string, schema: object) => {
  useEffect(() => {
    // Remove existing script with same id
    const existingScript = document.getElementById(id);
    if (existingScript) {
      existingScript.remove();
    }

    // Create new script element
    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.getElementById(id);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [id, JSON.stringify(schema)]);
};

export const WebsiteSchema = ({
  name = "Explaora",
  description = "Créez, documentez et partagez vos aventures de voyage avec Explaora.",
  url = "https://explaora.com",
}: WebsiteSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name,
    description,
    url,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${url}/explore?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  useStructuredData("schema-website", schema);
  return null;
};

export const ArticleSchema = ({
  title,
  description,
  image,
  datePublished,
  author,
  url,
}: ArticleSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    image,
    datePublished,
    author: {
      "@type": "Person",
      name: author,
    },
    publisher: {
      "@type": "Organization",
      name: "Explaora",
      logo: {
        "@type": "ImageObject",
        url: "https://explaora.com/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };

  useStructuredData("schema-article", schema);
  return null;
};

export const FAQSchema = ({ items }: FAQSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  useStructuredData("schema-faq", schema);
  return null;
};

export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: {
        "@type": "WebPage",
        "@id": item.url,
        name: item.name,
      },
    })),
  };

  useStructuredData("schema-breadcrumb", schema);
  return null;
};

export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Explaora",
    url: "https://explaora.com",
    logo: {
      "@type": "ImageObject",
      url: "https://explaora.com/logo.png",
      width: 512,
      height: 512,
    },
    description: "Plateforme de planification et partage de voyages",
    sameAs: [
      "https://www.instagram.com/explaora",
      "https://www.facebook.com/explaora",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      url: "https://explaora.com/contact",
    },
  };

  useStructuredData("schema-organization", schema);
  return null;
};

interface NavigationItem {
  name: string;
  url: string;
  description?: string;
}

interface SiteNavigationSchemaProps {
  items?: NavigationItem[];
}

export const SiteNavigationSchema = ({
  items = [
    { name: "Accueil", url: "https://explaora.com/fr", description: "Page d'accueil Explaora" },
    { name: "Explorer", url: "https://explaora.com/fr/explore", description: "Découvrir les voyages de la communauté" },
    { name: "Blog", url: "https://explaora.com/fr/blog", description: "Articles et conseils voyage" },
    { name: "Contact", url: "https://explaora.com/fr/contact", description: "Nous contacter" },
    { name: "FAQ", url: "https://explaora.com/fr/faq", description: "Questions fréquentes" },
  ],
}: SiteNavigationSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Navigation principale",
    itemListElement: items.map((item, index) => ({
      "@type": "SiteNavigationElement",
      position: index + 1,
      name: item.name,
      url: item.url,
      description: item.description,
    })),
  };

  useStructuredData("schema-site-navigation", schema);
  return null;
};

interface WebPageSchemaProps {
  url: string;
  name: string;
  description?: string;
  primaryImage?: string;
  datePublished?: string;
  dateModified?: string;
}

export const WebPageSchema = ({
  url,
  name,
  description,
  primaryImage,
  datePublished,
  dateModified,
}: WebPageSchemaProps) => {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": url,
    name,
    url,
  };

  if (description) schema.description = description;
  if (datePublished) schema.datePublished = datePublished;
  if (dateModified) schema.dateModified = dateModified;

  if (primaryImage) {
    schema.primaryImageOfPage = {
      "@type": "ImageObject",
      url: primaryImage,
      contentUrl: primaryImage,
    };
    schema.image = {
      "@type": "ImageObject",
      url: primaryImage,
    };
    schema.thumbnailUrl = primaryImage;
  }

  schema.isPartOf = {
    "@type": "WebSite",
    name: "Explaora",
    url: "https://explaora.com",
  };

  useStructuredData("schema-webpage", schema);
  return null;
};

interface TripStepSchema {
  city: string;
  latitude?: number | null;
  longitude?: number | null;
}

interface TripRouteSchema {
  from_step_id: string;
  to_step_id: string;
  transport_type?: string | null;
  travel_duration_minutes?: number | null;
}

interface TripSchemaProps {
  title: string;
  description?: string;
  image?: string;
  startDate?: string;
  endDate?: string;
  destinations: string[];
  url: string;
  reviewStats?: {
    averageRating: number;
    totalReviews: number;
  };
  steps?: TripStepSchema[];
  routes?: TripRouteSchema[];
  countries?: string[];
  characteristics?: string[] | null;
  budget?: number | null;
  budgetPublic?: boolean;
  tripType?: "trip" | "vacation";
}

const characteristicToTouristType: Record<string, string> = {
  "Solo": "Solo Traveler",
  "Entre amis": "Group",
  "En couple": "Couple",
  "En famille": "Family",
  "Adapté aux enfants": "Family",
  "Sportif": "Sports",
  "Aventure": "Adventure",
  "Culturel": "Cultural",
  "Détente": "Wellness",
  "Explo": "Explorer",
  "Fêtes": "Nightlife",
};

const computeISO8601Duration = (startDate?: string, endDate?: string): string | null => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return null;
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  return `P${days}D`;
};

const buildPlaceWithGeo = (step: TripStepSchema) => {
  const place: Record<string, unknown> = {
    "@type": "Place",
    name: step.city,
  };
  if (step.latitude != null && step.longitude != null) {
    place.geo = {
      "@type": "GeoCoordinates",
      latitude: step.latitude,
      longitude: step.longitude,
    };
  }
  return place;
};

export const TripSchema = ({
  title,
  description,
  image,
  startDate,
  endDate,
  destinations,
  url,
  reviewStats,
  steps = [],
  routes = [],
  countries = [],
  characteristics,
  budget,
  budgetPublic,
}: TripSchemaProps) => {
  const hasReviews = reviewStats && reviewStats.totalReviews > 0;
  const stepsWithGeo = steps.length > 0 ? steps : destinations.map(d => ({ city: d }));

  // Build main trip schema
  const tripSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": hasReviews ? "Product" : "TouristTrip",
    name: title,
    description: description || `Voyage: ${title}`,
    url,
  };

  if (image) {
    tripSchema.image = image;
  }

  if (hasReviews) {
    tripSchema.brand = { "@type": "Brand", name: "Explaora" };
    tripSchema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: reviewStats.averageRating,
      bestRating: 5,
      worstRating: 1,
      ratingCount: reviewStats.totalReviews,
    };
    // Add free offer for Product type
    tripSchema.offers = {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url,
    };
  } else {
    // TouristTrip-specific fields
    tripSchema.provider = {
      "@type": "Organization",
      name: "Explaora",
      url: "https://explaora.com",
    };

    if (startDate) tripSchema.startDate = startDate;
    if (endDate) tripSchema.endDate = endDate;

    const duration = computeISO8601Duration(startDate, endDate);
    if (duration) tripSchema.duration = duration;

    // Tourist types from characteristics
    const touristTypes = (characteristics || [])
      .map(c => characteristicToTouristType[c])
      .filter(Boolean);
    if (touristTypes.length > 0) {
      tripSchema.touristType = touristTypes;
    }

    // Countries
    if (countries.length > 0) {
      tripSchema.locationCreated = countries.length === 1
        ? { "@type": "Country", name: countries[0] }
        : countries.map(c => ({ "@type": "Country", name: c }));
    }

    // Itinerary with GPS
    if (stepsWithGeo.length > 0) {
      tripSchema.itinerary = {
        "@type": "ItemList",
        itemListElement: stepsWithGeo.map((step, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: buildPlaceWithGeo(step),
        })),
      };
    }
  }

  // Build TravelAction if we have steps
  const graphItems: Record<string, unknown>[] = [tripSchema];

  if (stepsWithGeo.length >= 2 && !hasReviews) {
    // Build step map for route resolution
    const stepMap = new Map<string, TripStepSchema>();
    steps.forEach((s, i) => {
      // Use index-based ID matching since we don't have IDs here
      stepMap.set(s.city, s);
    });

    const firstStep = stepsWithGeo[0];
    const lastStep = stepsWithGeo[stepsWithGeo.length - 1];

    const travelAction: Record<string, unknown> = {
      "@type": "TravelAction",
      name: `Voyager: ${firstStep.city} → ${lastStep.city}`,
      fromLocation: buildPlaceWithGeo(firstStep),
      toLocation: buildPlaceWithGeo(lastStep),
      result: {
        "@type": "WebPage",
        url,
      },
    };

    // Sub-actions from routes
    if (routes.length > 0 && steps.length > 0) {
      const stepById = new Map<string, TripStepSchema>();
      // We need step IDs - pass them through if available
      // For now, use ordered pairs from consecutive steps
      const subActions = routes
        .map(route => {
          const fromStep = steps.find((_, i) => {
            // Match by checking ordered steps
            return steps[i] && route.from_step_id;
          });
          const toStep = steps.find((_, i) => {
            return steps[i] && route.to_step_id;
          });
          // Use a simpler approach: map routes to consecutive step pairs
          return null;
        })
        .filter(Boolean);

      // Build sub-actions from consecutive steps instead
      const consecutiveActions: Record<string, unknown>[] = [];
      for (let i = 0; i < stepsWithGeo.length - 1; i++) {
        const fromStep = stepsWithGeo[i];
        const toStep = stepsWithGeo[i + 1];
        // Find matching route
        const matchingRoute = routes[i];
        const subAction: Record<string, unknown> = {
          "@type": "TravelAction",
          fromLocation: buildPlaceWithGeo(fromStep),
          toLocation: buildPlaceWithGeo(toStep),
        };
        if (matchingRoute?.transport_type) {
          subAction.instrument = matchingRoute.transport_type;
        }
        if (matchingRoute?.travel_duration_minutes) {
          const hours = Math.floor(matchingRoute.travel_duration_minutes / 60);
          const mins = matchingRoute.travel_duration_minutes % 60;
          subAction.duration = `PT${hours > 0 ? hours + 'H' : ''}${mins > 0 ? mins + 'M' : ''}`;
        }
        consecutiveActions.push(subAction);
      }

      if (consecutiveActions.length > 0) {
        travelAction.subAction = consecutiveActions;
      }
    }

    graphItems.push(travelAction);
  }

  // Use @graph format when we have multiple items
  const finalSchema = graphItems.length > 1
    ? { "@context": "https://schema.org", "@graph": graphItems }
    : tripSchema;

  useStructuredData("schema-trip", finalSchema);
  return null;
};
